import { useMemo } from 'react';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { getUnitSymbols } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTableColumn } from '~/exportReport/table/ReportTable.model';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import type { ExportReportRadiusParams } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';

export const getExportReportLocationRadiusTableColumns = (translate: TranslationFnc) => [{
  type: 'radius',
  id: '5',
  heading: '5mi',
  secondaryHeading: translate('Radius Circle'),
  unit: UnitSystem.imperial,
  distance: 5,
}, {
  type: 'radius',
  id: '10',
  heading: '10mi',
  secondaryHeading: translate('Radius Circle'),
  unit: UnitSystem.imperial,
  distance: 10,
}, {
  type: 'radius',
  id: '15',
  heading: '15mi',
  secondaryHeading: translate('Radius Circle'),
  unit: UnitSystem.imperial,
  distance: 15,
}] as const satisfies ReadonlyArray<ReportTableColumn>;

export const getExportReportRadiusTableColumns = (
  params: ExportReportRadiusParams,
  translate: TranslationFnc
): ReadonlyArray<ReportTableColumn> => {
  const unit = getUnitSymbols(params.data.unit).unit;

  return [{
    type: 'radius',
    id: 'radius',
    heading: `${params.data.radius}${unit}`,
    secondaryHeading: translate('Radius Circle'),
    unit: params.data.unit,
    distance: params.data.radius,
  }];
};

export const useExportReportParams = () => {
  const exportReportState = useToolsStateExportReportSelector();
  const [t] = useTranslation();

  const tableColumns = useMemo<ReadonlyArray<ReportTableColumn>>(() => {
    if (!exportReportState.isActive) {
      return [];
    }

    // Sample reports display the columns of the report type they mimic.
    const params = exportReportState.settings.sample?.visualParams ?? exportReportState.params;

    switch (params.type) {
      case ExportReportType.Location:
        return getExportReportLocationRadiusTableColumns(t);
      case ExportReportType.Radius:
        return getExportReportRadiusTableColumns(params, t);

      case ExportReportType.DriveTimePolygon: {
        return [{
          type: 'polygon',
          id: 'dtp',
          heading: `${params.data.minutes} min`,
          secondaryHeading: t('DT Polygon'),
        }];
      }

      case ExportReportType.Polygon: {
        return [{
          type: 'polygon',
          id: 'boundary',
          heading: params.data.heading,
          secondaryHeading: t('Boundary'),
        }];
      }

      default: {
        return [];
      }
    }
  }, [exportReportState, t]);

  return { tableColumns };
};
